import React, { Fragment, FunctionComponent, ReactNode } from 'react'

import { FormControl } from '@/components/form-elements'
import { DisplayType, Field, FormControlType } from '@/models/common'
import { Space } from '@/styles'

import FieldArray from '../fields/field-array'
import { FieldArrayPreview, FieldWrapper, Section, SectionDescription, SectionTitle } from './atoms'

interface Props {
  sectionTitle?: string
  sectionTooltip?: string
  sectionDescription?: string
  fields: Array<Field>
  displayType: DisplayType
}

const renderPreviewFields = (fields): ReactNode => {
  return (
    <Fragment>
      {fields.map(
        (
          { label, width, valueFormat, value, hideField = false, fields, fieldArrayTitle, decimalScale, tooltip, color, currency },
          index,
        ) => {
          if (hideField) return null

          if (Array.isArray(value)) {
            return <FieldArrayPreview key={`${label}-${index}`} values={value} fields={fields} fieldArrayTitle={fieldArrayTitle} />
          }

          return (
            <FieldWrapper width={width} key={`${label}-${index}`}>
              <FormControl
                label={label}
                control={FormControlType.DISPLAY}
                fieldValue={value}
                valueFormat={valueFormat}
                decimalScale={decimalScale}
                tooltip={tooltip}
                color={color}
                currency={currency}
              />
            </FieldWrapper>
          )
        },
      )}
    </Fragment>
  )
}

const renderFormFields = (fields): ReactNode => {
  return (
    <Fragment>
      {fields.map(
        (
          {
            name,
            value,
            label,
            width,
            hideField = false,
            fields,
            addFieldArrayButtonText,
            removeFieldArrayButtonText,
            control,
            fieldArrayTitle,
            initialArrayValues,
            tooltip,
            ...rest
          },
          index,
        ) => {
          if (hideField) return null

          if (Array.isArray(value) && control !== FormControlType.DISPLAY) {
            return (
              <FieldArray
                key={name}
                values={value as Array<{ [field: string]: string | number }>}
                name={name}
                fields={fields}
                addButtonText={addFieldArrayButtonText}
                removeButtonText={removeFieldArrayButtonText}
                fieldArrayTitle={fieldArrayTitle}
                initialArrayValues={initialArrayValues}
              />
            )
          }

          if (Array.isArray(value) && control === FormControlType.DISPLAY) {
            return <FieldArrayPreview key={`${label}-${index}`} values={value} fields={fields} fieldArrayTitle={fieldArrayTitle} />
          }

          return (
            <FieldWrapper width={width} key={`${label}-${index}`} mb={Space.S12}>
              <FormControl label={label} name={name} control={control} fieldValue={value} tooltip={tooltip} {...rest} />
            </FieldWrapper>
          )
        },
      )}
    </Fragment>
  )
}

const FormSection: FunctionComponent<Props> = ({ sectionTitle, sectionDescription, fields, displayType, sectionTooltip }: Props) => {
  const allFieldsAreHided = fields.every(field => field.hideField)
  if (allFieldsAreHided) return null

  return (
    <Section>
      {sectionTitle && (
        <SectionTitle sectionDescription={sectionDescription} sectionTooltip={sectionTooltip}>
          {sectionTitle}
        </SectionTitle>
      )}
      {sectionDescription && <SectionDescription>{sectionDescription}</SectionDescription>}
      {displayType === DisplayType.PREVIEW ? renderPreviewFields(fields) : renderFormFields(fields)}
    </Section>
  )
}

FormSection.displayName = 'FormSection'

export default FormSection
